import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { map, reduce } from 'lodash';
import classnames from 'classnames';

import {
	storageStackedUnitBuckets,
	vehicleStackedUnitBuckets,
} from 'config/units';

import pluralize from 'utils/pluralize';
import { Link } from 'components/core/Link';
import StackedUnit from './StackedUnit';

import './StackedUnits.scss';

export function emptyStorageUnit(size) {
	const unit = {
		quantity: 0,
		placeholder: true,
	};
	switch (size) {
		case 'largeUnit':
			unit.size = "10' x 10'";
			break;
		case 'mediumUnit':
			unit.size = "5' x 10'";
			break;
		default:
			unit.size = "5' x 5'";
	}

	return unit;
}

export function StackedUnits({
	facility,
	units,
	storageType,
	showRecommendedUnit,
	withScarcity,
	className,
}) {
	const { stackedUnits, unitsAvailable, facilityId, facilityURL } =
		useMemo(() => {
			let stacked = [];

			if (showRecommendedUnit && units.recommendedUnit) {
				stacked = [units.recommendedUnit];
			} else if (storageType === 'vehicle') {
				// Grab the first 3 available vehicle buckets
				stacked = reduce(
					vehicleStackedUnitBuckets,
					(stack, bucket) =>
						units[bucket] ? [...stack, units[bucket]] : stack,
					[],
				).slice(0, 3);
			} else {
				// create size buckets with placeholders
				stacked = map(
					storageStackedUnitBuckets,
					(size) => units[size] || emptyStorageUnit(size),
				);
			}

			return {
				stackedUnits: stacked,
				unitsAvailable: units.unitsAvailable,
				moreUnitsText: units.moreUnitsText,
				facilityId: facility.id,
				facilityURL: facility.url,
			};
		}, [units, storageType, showRecommendedUnit, facility]);

	return (
		<div className={classnames('stacked-units', className)}>
			{stackedUnits.map((unit, index) => (
				<StackedUnit
					key={unit.id || `${facilityId}-${index}`}
					facilityId={facilityId}
					unit={unit}
					withScarcity={withScarcity}
				/>
			))}
			<Link
				secondary
				className="view-more-units"
				to={`${facilityURL}#units`}
			>
				Compare{' '}
				<strong>
					{unitsAvailable} {pluralize('unit', unitsAvailable)}{' '}
				</strong>{' '}
				at this facility &raquo;
			</Link>
		</div>
	);
}

StackedUnits.propTypes = {
	className: PropTypes.string,
	facility: PropTypes.object.isRequired,
	units: PropTypes.object.isRequired,
	storageType: PropTypes.string,
	showRecommendedUnit: PropTypes.bool,
	withScarcity: PropTypes.bool,
};

export default StackedUnits;
